import type { FC, ReactNode } from 'react'
import type { Movement, MovementType } from '@/types'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import MovementTypeBadge from '@/components/inventory/movement-type-badge'
import UnitLabel from '@/components/inventory/unit-label'
import DateTime from '@/components/generals/date-time'

interface MovementListProps {
    movements: Movement[]
    showProduct?: boolean
    caption?: string
}

interface MovementItemProps {
    movement: Movement
    showProduct: boolean
}

const QUANTITY_STYLES: Record<MovementType, string> = {
    entrada: 'text-flow-in-ink',
    salida: 'text-flow-out-ink',
    ajuste: 'text-flow-adjust-ink'
}

const ICON_STYLES: Record<MovementType, string> = {
    entrada: 'bg-flow-in-soft text-flow-in-ink',
    salida: 'bg-flow-out-soft text-flow-out-ink',
    ajuste: 'bg-flow-adjust-soft text-flow-adjust-ink'
}

const MovementQuantity: FC<{ movement: Movement, className?: string }> = ({ movement, className }): ReactNode => {
    const { signed } = useSettings()

    return (
        <span className={cn('font-semibold whitespace-nowrap tabular-nums', QUANTITY_STYLES[movement.type], className)}>
            <span>{signed(movement.quantityChange)}</span> <UnitLabel productId={movement.productId} unit={movement.productUnit} className='font-normal' />
        </span>
    )
}

const MovementReason: FC<{ movement: Movement }> = ({ movement }): ReactNode => {
    return (
        <div className='min-w-0'>
            <p className='truncate text-sm text-foreground'>
                {movement.reason ? (
                    <span data-mdlfy-cms={`stock_movements:${movement.id}:reason`}>{movement.reason}</span>
                ) : (
                    <span className='text-muted-foreground'>Sin motivo</span>
                )}
            </p>

            {movement.note && (
                <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>
                    <span data-mdlfy-cms={`stock_movements:${movement.id}:note`}>{movement.note}</span>
                </p>
            )}
        </div>
    )
}

const ProductCell: FC<{ movement: Movement }> = ({ movement }): ReactNode => {
    return (
        <div className='min-w-0'>
            <Link
                href={`/inventario/${movement.productId}`}
                className='block truncate text-sm font-medium text-foreground hover:text-primary hover:underline underline-offset-4'
            >
                <span data-mdlfy-cms={`products:${movement.productId}:name`}>{movement.productName}</span>
            </Link>

            {movement.productSku && (
                <p className='mt-0.5 truncate font-mono text-xs text-muted-foreground'>
                    <span data-mdlfy-cms={`products:${movement.productId}:sku`}>{movement.productSku}</span>
                </p>
            )}
        </div>
    )
}

const MovementRow: FC<MovementItemProps> = ({ movement, showProduct }): ReactNode => {
    const { number } = useSettings()

    return (
        <tr className='border-b last:border-b-0 hover:bg-muted/40'>
            <td className='py-3 pr-3 pl-4 align-top text-sm text-muted-foreground tabular-nums sm:pl-5'>
                <DateTime value={movement.createdAt} />
            </td>

            {showProduct && (
                <td className='px-3 py-3 align-top'>
                    <ProductCell movement={movement} />
                </td>
            )}

            <td className='px-3 py-3 align-top'>
                <MovementTypeBadge type={movement.type} />
            </td>

            <td className='px-3 py-3 align-top'>
                <MovementReason movement={movement} />
            </td>

            <td className='py-3 pr-4 pl-3 text-right align-top text-sm sm:pr-5'>
                <MovementQuantity movement={movement} />

                {movement.stockAfter !== null && (
                    <p className='mt-0.5 text-xs whitespace-nowrap text-muted-foreground tabular-nums'>
                        Stock: <span>{number(movement.stockAfter)}</span> <UnitLabel productId={movement.productId} unit={movement.productUnit} />
                    </p>
                )}
            </td>
        </tr>
    )
}

const MovementCard: FC<MovementItemProps> = ({ movement, showProduct }): ReactNode => {
    const { number } = useSettings()
    const Icon = MOVEMENT_ICONS[movement.type]

    return (
        <li className='flex items-start gap-3 px-4 py-3.5'>
            <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full', ICON_STYLES[movement.type])}>
                <Icon className='size-4' aria-hidden='true' />
            </span>

            <div className='min-w-0 flex-1'>
                <div className='flex items-start justify-between gap-3'>
                    {showProduct ? <ProductCell movement={movement} /> : <MovementReason movement={movement} />}
                    <MovementQuantity movement={movement} className='text-sm' />
                </div>

                {showProduct && (
                    <div className='mt-1'>
                        <MovementReason movement={movement} />
                    </div>
                )}

                <p className='mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground'>
                    <DateTime value={movement.createdAt} />

                    {movement.stockAfter !== null && (
                        <span className='tabular-nums'>
                            Stock: <span>{number(movement.stockAfter)}</span> <UnitLabel productId={movement.productId} unit={movement.productUnit} />
                        </span>
                    )}
                </p>
            </div>
        </li>
    )
}

const MovementList: FC<MovementListProps> = ({
    movements,
    showProduct = true,
    caption = 'Movimientos de stock'
}): ReactNode => {
    return (
        <>
            <div className='hidden md:block'>
                <table className='w-full table-fixed border-collapse text-left'>
                    <caption className='sr-only'>
                        {caption}
                    </caption>

                    <thead className='border-b bg-muted/50 text-xs text-muted-foreground'>
                        <tr>
                            <th scope='col' className='w-32 py-2.5 pr-3 pl-4 font-medium sm:pl-5 xl:w-40'>Fecha</th>
                            {showProduct && <th scope='col' className='px-3 py-2.5 font-medium'>Producto</th>}
                            <th scope='col' className='w-28 px-3 py-2.5 font-medium'>Tipo</th>
                            <th scope='col' className='px-3 py-2.5 font-medium'>Motivo</th>
                            <th scope='col' className='w-36 py-2.5 pr-4 pl-3 text-right font-medium sm:pr-5'>Cantidad</th>
                        </tr>
                    </thead>

                    <tbody>
                        {movements.map(movement => (
                            <MovementRow key={movement.id} movement={movement} showProduct={showProduct} />
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className='divide-y md:hidden'>
                {movements.map(movement => (
                    <MovementCard key={movement.id} movement={movement} showProduct={showProduct} />
                ))}
            </ul>
        </>
    )
}

export default MovementList
